import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { 
  FaCar, 
  FaUser, 
  FaExchangeAlt, 
  FaFileInvoiceDollar, 
  FaCheckCircle, 
  FaFileContract, 
  FaPaperPlane, 
  FaLock, 
  FaCopy, 
  FaRobot, 
  FaPlus, 
  FaArrowLeft, 
  FaSignOutAlt, 
  FaExternalLinkAlt, 
  FaCheck,
  FaQrcode,
  FaTimes,
  FaTabletAlt
} from "react-icons/fa";

const D2Deals = () => {
  const navigate = useNavigate();
const getCarImageUrl = (vehicle, selectedColorName) => {
  if (!vehicle) return "";
  let img = vehicle.imageUrl;
  if (selectedColorName && vehicle.colors && vehicle.colors.length > 0) {
    const matchedColor = vehicle.colors.find(c => c.name === selectedColorName);
    if (matchedColor && matchedColor.imageUrl) {
      img = matchedColor.imageUrl;
    }
  }
  return img || "https://images.unsplash.com/photo-1549399542-7e3f8b79c341?auto=format&fit=crop&q=80&w=400";
};

  const [deals, setDeals] = useState([]);
  const [cars, setCars] = useState([]);
  const [fandiList, setFandiList] = useState([]);
  const [plans, setPlans] = useState([]);
  const [rebatesList, setRebatesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDealId, setActiveDealId] = useState(null);
  
  const [dealState, setDealState] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showKioskQrModal, setShowKioskQrModal] = useState(false);
  const [copiedKiosk, setCopiedKiosk] = useState(false);
  const [claimId, setClaimId] = useState("");

  const fetchDealsAndCars = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const [dealsRes, carsRes, fandiRes, plansRes, rebatesRes] = await Promise.all([
        fetch(`${getBackendBaseUrl()}/d2-deals`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${getBackendBaseUrl()}/d2-cars`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${getBackendBaseUrl()}/d2-fandi`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${getBackendBaseUrl()}/d2-lenders/plans`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${getBackendBaseUrl()}/d2-rebates`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const dealsData = await dealsRes.json();
      const carsData = await carsRes.json();
      const fandiData = await fandiRes.json();
      const plansData = await plansRes.json();
      const rebatesData = await rebatesRes.json();
      
      if (dealsData.success) setDeals(dealsData.data || []);
      if (carsData.success) setCars(carsData.data || []);
      if (fandiData.success) setFandiList(fandiData.data || []);
      if (plansData.success) setPlans(plansData.data || []);
      if (rebatesData.success) setRebatesList(rebatesData.data || []);
    } catch (err) {
      console.error("Failed to fetch data", err);
      toast.error("Failed to load deals data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDealsAndCars();
  }, []);

  const handleCreateDraft = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({}) // Empty body for draft
      });
      const data = await res.json();
      if (data.success) {
        setDealState(data.data);
        setActiveDealId(data.data._id);
        toast.success("New deal draft created.");
      } else {
        toast.error("Failed to create draft");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error creating draft");
    }
  };

  const handleResumeDeal = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setDealState(data.data);
        setActiveDealId(id);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error resuming deal");
    }
  };

  const handleClaimDeal = async () => {
    if (!claimId.trim()) {
      toast.error("Please enter a Deal Number to claim.");
      return;
    }
    
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/claim`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ dealNumber: claimId })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || "Successfully claimed deal!");
        setClaimId("");
        // Reload list and directly open the workspace for that deal
        await fetchDealsAndCars();
        handleResumeDeal(data.data._id);
      } else {
        toast.error(data.message || "Failed to claim deal.");
      }
    } catch (err) {
      toast.error("Network error claiming deal.");
    }
  };

  const handleAutosave = async (updates) => {
    if (!activeDealId) return;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/${activeDealId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (data.success) {
        setDealState(data.data); // Updates UI with latest calculation from backend
      } else {
        toast.error("Autosave failed: " + data.message);
      }
    } catch (err) {
      console.error("Autosave error", err);
    }
  };

  // UI Handlers for Steps
  const setStep = (stepName) => handleAutosave({ currentStep: stepName });

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "DRAFT":
        return "bg-slate-50 text-slate-600 border-slate-200";
      case "IN_PROGRESS":
        return "bg-blue-50 text-blue-700 border-blue-200/50";
      case "APPROVED":
        return "bg-emerald-50 text-emerald-700 border-emerald-200/50";
      case "LENDER_SUBMITTED":
        return "bg-purple-50 text-purple-700 border-purple-200/50";
      case "COMPLETED":
        return "bg-teal-50 text-teal-700 border-teal-200/50";
      default:
        return "bg-slate-50 text-slate-600 border-slate-200";
    }
  };

  if (activeDealId && dealState) {
    let step = dealState.currentStep || "VEHICLE";
    
    // Override step if deal has advanced past structuring
    if (dealState.status !== "DRAFT" && dealState.status !== "IN_PROGRESS" && dealState.status !== "DEAL_STRUCTURED") {
      step = dealState.status;
    }

    const linkUrl = `${window.location.origin}/live/${dealState.customerLiveLink?.token}`;
    const kioskUrl = `${window.location.origin}/kiosk/${dealState.customerLiveLink?.token}`;

    const stepsList = [
      { id: "VEHICLE", label: "Vehicle", icon: FaCar },
      { id: "CUSTOMER_INFO", label: "Customer", icon: FaUser },
      { id: "TRADE", label: "Trade", icon: FaExchangeAlt },
      { id: "FINANCE", label: "Finance", icon: FaFileInvoiceDollar },
      { id: "DEAL_STRUCTURING", label: "Review", icon: FaCheckCircle },
      { id: "DOCUMENTATION", label: "Docs", icon: FaFileContract },
      { id: "LENDER_SUBMITTED", label: "Underwrite", icon: FaPaperPlane },
      { id: "COMPLETED", label: "Delivery", icon: FaCheckCircle }
    ];

    const currentStepIndex = stepsList.findIndex(s => s.id === step);

    return (
      <div className="flex h-full w-full bg-slate-50/50 overflow-hidden animate-fade-in-up">
        {/* Main Workspace Area */}
        <div className="flex-1 flex flex-col h-full overflow-hidden p-8">
          {/* Header Bar */}
          <div className="flex justify-between items-center mb-6 flex-shrink-0">
            <div>
              <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight flex items-center gap-2">
                Deal Workspace
                <span className="text-slate-400 font-normal text-base">#{dealState.dealNumber}</span>
              </h1>
            </div>
            <button 
              onClick={() => { setActiveDealId(null); fetchDealsAndCars(); }} 
              className="text-slate-500 hover:text-slate-800 font-semibold text-xs tracking-wide bg-white hover:bg-slate-50 border border-slate-200 shadow-sm px-4 py-2 rounded-xl transition-all flex items-center gap-1.5"
            >
              <FaSignOutAlt size={12} /> Save & Exit
            </button>
          </div>

          {/* Stepper Header */}
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-4 mb-6 flex-shrink-0 overflow-x-auto scrollbar-none">
            <div className="flex items-center justify-between min-w-[700px] px-2">
              {stepsList.map((s, idx) => {
                const StepIcon = s.icon;
                const isPassed = idx < currentStepIndex;
                const isActive = s.id === step;
                
                return (
                  <React.Fragment key={s.id}>
                    <div className="flex flex-col items-center gap-1 group">
                      <div className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all ${
                        isActive 
                          ? 'bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-200' 
                          : isPassed 
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-600' 
                            : 'bg-slate-50 border-slate-200 text-slate-400'
                      }`}>
                        {isPassed ? <FaCheck size={10} /> : <StepIcon size={12} />}
                      </div>
                      <span className={`text-[10px] font-bold tracking-tight uppercase ${isActive ? 'text-indigo-600' : 'text-slate-400'}`}>{s.label}</span>
                    </div>
                    {idx < stepsList.length - 1 && (
                      <div className={`h-[2px] flex-1 mx-2 transition-all rounded ${
                        idx < currentStepIndex ? 'bg-emerald-500' : 'bg-slate-100'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Interactive Steps Card Wrapper */}
          <div className="flex-1 overflow-y-auto pr-1">
            <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-8 max-w-3xl mx-auto mb-6">
              
              {/* VEHICLE STEP */}
              {step === "VEHICLE" && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                      <span className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">🤖</span> Select a Vehicle
                    </h2>
                    <p className="text-slate-400 text-xs mt-1">Select from the dealership car master data inventory.</p>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase mb-2">Search Stock / VIN / Make Model</label>
                    <select 
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all"
                      onChange={(e) => {
                        if (e.target.value) handleAutosave({ carId: e.target.value });
                      }}
                      value={dealState.vehicle?.stockNumber ? cars.find(c => c.stockNumber === dealState.vehicle.stockNumber)?._id : ""}
                    >
                      <option value="">-- Click to choose from inventory --</option>
                      {cars.map(c => (
                        <option key={c._id} value={c._id}>
                          {c.year} {c.make} {c.model} - ${c.salePrice?.toLocaleString()} ({c.stockNumber})
                        </option>
                      ))}
                    </select>
                  </div>
                  {dealState.vehicle?.vin && (
                    <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up">
                      <div>
                        <p className="font-bold text-slate-800">{dealState.vehicle.year} {dealState.vehicle.make} {dealState.vehicle.model}</p>
                        <p className="text-xs text-slate-400 mt-1 font-mono">VIN: {dealState.vehicle.vin}</p>
                      </div>
                      <button 
                        onClick={() => setStep("CUSTOMER_INFO")} 
                        className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl text-xs font-bold hover:shadow-lg shadow-md transition-all self-end sm:self-auto"
                      >
                        Continue &rarr;
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* CUSTOMER INFO STEP */}
              {step === "CUSTOMER_INFO" && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                      <span className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">🤖</span> Customer Profile
                    </h2>
                    <p className="text-slate-400 text-xs mt-1">Configure customer personal identifiers.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Customer Full Name</label>
                      <input 
                        type="text" 
                        className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all"
                        placeholder="e.g. Robert Smith"
                        value={dealState.customer?.name || ""}
                        onChange={(e) => handleAutosave({ customer: { ...dealState.customer, name: e.target.value } })}
                        onBlur={() => handleAutosave({ customer: dealState.customer })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Phone Number</label>
                      <input 
                        type="text" 
                        className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all"
                        placeholder="(555) 555-5555"
                        value={dealState.customer?.phone || ""}
                        onChange={(e) => handleAutosave({ customer: { ...dealState.customer, phone: e.target.value } })}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between mt-8 pt-4 border-t border-slate-100">
                    <button onClick={() => setStep("VEHICLE")} className="text-slate-500 hover:text-slate-800 font-semibold text-sm">Back</button>
                    <button onClick={() => setStep("TRADE")} className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl text-xs font-bold hover:shadow-lg shadow-md transition-all">Continue &rarr;</button>
                  </div>
                </div>
              )}

              {/* TRADE STEP */}
              {step === "TRADE" && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                      <span className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">🤖</span> Vehicle Trade-In
                    </h2>
                    <p className="text-slate-400 text-xs mt-1">Determine if client is trading an existing vehicle.</p>
                  </div>
                  <div className="flex gap-4">
                    <button 
                      onClick={() => handleAutosave({ trade: { ...dealState.trade, hasTrade: true } })} 
                      className={`flex-1 py-4 border rounded-xl font-bold transition-all ${
                        dealState.trade?.hasTrade 
                          ? 'bg-indigo-50 border-indigo-600 text-indigo-700 shadow-sm' 
                          : 'hover:bg-slate-50 border-slate-200 text-slate-600'
                      }`}
                    >
                      Yes, Client Has Trade
                    </button>
                    <button 
                      onClick={() => handleAutosave({ trade: { ...dealState.trade, hasTrade: false } })} 
                      className={`flex-1 py-4 border rounded-xl font-bold transition-all ${
                        dealState.trade?.hasTrade === false 
                          ? 'bg-indigo-50 border-indigo-600 text-indigo-700 shadow-sm' 
                          : 'hover:bg-slate-50 border-slate-200 text-slate-600'
                      }`}
                    >
                      No Trade-In
                    </button>
                  </div>
                  
                  {dealState.trade?.hasTrade && (
                    <div className="space-y-4 bg-slate-50 p-5 rounded-2xl border border-slate-100 animate-fade-in-up">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Trade Allowance (Market Value)</label>
                          <input 
                            type="number" 
                            className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all"
                            value={dealState.trade.allowance || ""}
                            onChange={(e) => handleAutosave({ trade: { ...dealState.trade, allowance: Number(e.target.value) } })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Current Payoff (What is Owed)</label>
                          <input 
                            type="number" 
                            className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all"
                            value={dealState.trade.payoff || ""}
                            onChange={(e) => handleAutosave({ trade: { ...dealState.trade, payoff: Number(e.target.value) } })}
                          />
                        </div>
                      </div>
                      <div className="p-4 bg-white border border-slate-200/80 rounded-xl text-center font-bold text-slate-700 shadow-sm text-sm">
                        Trade Equity Value: <span className={dealState.calculation?.tradeEquity < 0 ? "text-red-600" : "text-emerald-600"}>${dealState.calculation?.tradeEquity?.toLocaleString() || 0}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-between mt-8 pt-4 border-t border-slate-100">
                    <button onClick={() => setStep("CUSTOMER_INFO")} className="text-slate-500 hover:text-slate-800 font-semibold text-sm">Back</button>
                    <button onClick={() => setStep("FINANCE")} className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl text-xs font-bold hover:shadow-lg shadow-md transition-all">Continue &rarr;</button>
                  </div>
                </div>
              )}

              {/* FINANCE STEP */}
              {step === "FINANCE" && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                      <span className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">🤖</span> Deal Structuring & Finance
                    </h2>
                    <p className="text-slate-400 text-xs mt-1">Configure credit plan guidelines and F&I products.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Down Payment ($)</label>
                      <input 
                        type="number" 
                        className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all"
                        value={dealState.finance?.downPayment || ""}
                        onChange={(e) => handleAutosave({ finance: { ...dealState.finance, downPayment: Number(e.target.value) } })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Manufacturer Rebate / Coupon</label>
                      <select 
                        className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all"
                        value={dealState.finance?.couponCode || ""}
                        onChange={(e) => {
                          const selectedCode = e.target.value;
                          const matched = rebatesList.find(r => r.code === selectedCode);
                          if (matched) {
                            handleAutosave({ finance: { ...dealState.finance, couponCode: matched.code, rebate: matched.discountAmount } });
                          } else {
                            handleAutosave({ finance: { ...dealState.finance, couponCode: "", rebate: 0 } });
                          }
                        }}
                      >
                        <option value="">-- Select Rebate Program ($0) --</option>
                        {rebatesList.map(r => (
                          <option key={r._id} value={r.code}>
                            {r.code} - {r.name} (${r.discountAmount.toLocaleString()} Off)
                          </option>
                        ))}
                      </select>
                      <div className="mt-1 flex justify-between items-center text-[11px] text-slate-400">
                        <span>Rebate Applied: <strong className="text-emerald-600 font-bold">${(dealState.finance?.rebate || 0).toLocaleString()}</strong></span>
                        {dealState.finance?.couponCode && <span className="text-indigo-600 font-mono font-bold bg-indigo-50 px-1.5 py-0.5 rounded">{dealState.finance.couponCode}</span>}
                      </div>
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Select Lender Plan</label>
                      <select 
                        className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all"
                        value={dealState.lender?.planId?._id || dealState.lender?.planId || ""}
                        onChange={(e) => handleAutosave({ lender: { ...dealState.lender, planId: e.target.value } })}
                      >
                        <option value="">-- Choose a Plan --</option>
                        {plans.map(p => (
                          <option key={p._id} value={p._id}>
                            {p.lenderId?.name || "Lender"} - {p.name} ({p.apr}% for {p.term} mos)
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Lender APR (%)</label>
                      <input 
                        disabled 
                        type="number" 
                        step="0.1" 
                        className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-100 text-slate-400 font-semibold cursor-not-allowed" 
                        value={dealState.lender?.apr || ""}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Term (Months)</label>
                      <input 
                        disabled 
                        type="number" 
                        className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-100 text-slate-400 font-semibold cursor-not-allowed" 
                        value={dealState.lender?.term || ""}
                      />
                    </div>
                  </div>

                  {/* F&I Products subsection */}
                  <div className="mt-6 p-5 bg-slate-50 border border-slate-100 rounded-2xl">
                     <h3 className="text-xs font-bold text-slate-600 uppercase mb-3 tracking-wider">F&I Protection Products</h3>
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                       {fandiList.map(prod => {
                         const selectedProductIds = (dealState.fAndI?.products || []).map(p => p._id || p);
                         const isSelected = selectedProductIds.includes(prod._id);
                         
                         return (
                           <div key={prod._id} className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200/60 shadow-sm hover:border-indigo-100 transition-colors">
                             <div className="pr-2">
                               <div className="font-bold text-slate-800 text-xs truncate max-w-[180px]">{prod.name}</div>
                               <div className="text-[10px] font-bold text-emerald-600 mt-0.5">${prod.price?.toLocaleString()}</div>
                             </div>
                             <button 
                               onClick={() => {
                                 const updatedProducts = isSelected 
                                   ? selectedProductIds.filter(id => id !== prod._id)
                                   : [...selectedProductIds, prod._id];
                                 handleAutosave({ fAndI: { ...dealState.fAndI, products: updatedProducts } });
                               }}
                               className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                 isSelected 
                                   ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200/30' 
                                   : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200/30'
                               }`}
                             >
                               {isSelected ? '✓ Added' : '+ Add'}
                             </button>
                           </div>
                         );
                       })}
                     </div>
                  </div>

                  <div className="flex justify-between mt-8 pt-4 border-t border-slate-100">
                    <button onClick={() => setStep("TRADE")} className="text-slate-500 hover:text-slate-800 font-semibold text-sm">Back</button>
                    <button onClick={() => setStep("DEAL_STRUCTURING")} className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl text-xs font-bold hover:shadow-lg shadow-md transition-all">Final Review &rarr;</button>
                  </div>
                </div>
              )}

              {/* DEAL STRUCTURING STEP (Final Review) */}
              {step === "DEAL_STRUCTURING" && (
                <div className="space-y-6 text-center py-6">
                  <div className="w-16 h-16 bg-emerald-50 text-emerald-500 border border-emerald-100 rounded-full flex items-center justify-center text-2xl mx-auto shadow-md">
                    ✓
                  </div>
                  <div>
                    <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight">Structure Finalized</h2>
                    <p className="text-slate-400 text-sm mt-2 max-w-sm mx-auto">
                      Terms are aligned. The customer live link is updated instantly to display payment parameters.
                    </p>
                  </div>
                  
                  <div className="flex justify-center items-center gap-4 pt-6">
                    <button 
                      onClick={() => setStep("FINANCE")} 
                      className="px-5 py-2.5 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 hover:text-slate-800 font-semibold text-sm transition-all"
                    >
                      Edit Structuring
                    </button>
                    <button 
                      onClick={() => handleAutosave({ status: "DOCUMENTATION" })} 
                      className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl text-white font-bold hover:shadow-lg shadow-md transition-all text-sm"
                    >
                      Proceed to Documentation
                    </button>
                  </div>
                </div>
              )}

              {/* DOCUMENTATION STEP */}
              {step === "DOCUMENTATION" && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                      <span className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">📄</span> Document Execution
                    </h2>
                    <p className="text-slate-400 text-xs mt-1">Review locked contract forms and disclosures.</p>
                  </div>
                  
                  <div className="space-y-2">
                    {dealState.documents?.map((doc, idx) => (
                      <div key={idx} className="p-4 bg-slate-50 border border-slate-200/60 rounded-xl flex justify-between items-center text-sm shadow-sm hover:border-slate-300 transition-colors">
                        <span className="font-bold text-slate-700">{doc}</span>
                        <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-lg text-xs font-semibold">Ready to Sign</span>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-200/50 rounded-2xl text-xs text-amber-800 flex items-start gap-2.5">
                    <span className="text-base select-none">ℹ️</span>
                    <p className="leading-relaxed">
                      Editing is locked. To modify rates, prices or structures, click <strong>Unlock & Recalculate</strong> to invalidate generated agreements.
                    </p>
                  </div>

                  <div className="flex justify-between pt-5 border-t border-slate-100">
                    <button 
                      onClick={() => handleAutosave({ status: "DRAFT", currentStep: "FINANCE" })} 
                      className="text-red-500 hover:text-red-700 font-bold text-xs tracking-wide bg-red-50 hover:bg-red-100 px-4 py-2 rounded-xl transition-all border border-red-200/20"
                    >
                      Unlock & Recalculate
                    </button>
                    <button 
                      onClick={() => handleAutosave({ status: "LENDER_SUBMITTED" })} 
                      className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-5 py-2 rounded-xl text-xs font-bold hover:shadow-lg shadow-md transition-all"
                    >
                      Submit to Lender
                    </button>
                  </div>
                </div>
              )}

              {/* LENDER SUBMITTED STEP */}
              {step === "LENDER_SUBMITTED" && (
                <div className="space-y-6 text-center py-4">
                  <div className="w-12 h-12 bg-purple-50 text-purple-600 border border-purple-100 rounded-full flex items-center justify-center text-xl mx-auto animate-pulse">
                    📡
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800 tracking-tight">Lender Underwriting Status</h2>
                  </div>
                  
                  {dealState.lender?.status === "STIPULATIONS" && (
                    <div className="space-y-4 animate-fade-in-up">
                      <div className="p-4 bg-amber-50 border border-amber-200/50 rounded-2xl text-amber-800 text-xs text-left leading-relaxed">
                        <strong>⚠️ Stipulations Required:</strong> The lender underwriter requires verified <strong>Proof of Income</strong> (paystub document) to proceed.
                      </div>
                      <div className="border-2 border-dashed border-slate-200 bg-slate-50/50 p-8 rounded-2xl flex flex-col items-center justify-center gap-4">
                        <span className="text-xs text-slate-400 font-medium">Underwriting Simulation Payload</span>
                        <button 
                          onClick={() => handleAutosave({ stipulationFileUploaded: true })} 
                          className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold text-xs hover:shadow-lg shadow-md transition-all"
                        >
                          Upload Simulated Income Document
                        </button>
                      </div>
                    </div>
                  )}

                  {dealState.lender?.status === "DECLINED" && (
                    <div className="space-y-4 animate-fade-in-up">
                      <div className="p-4 bg-red-50 border border-red-200/50 rounded-2xl text-red-800 text-xs text-left leading-relaxed">
                        <strong>❌ Deal Declined:</strong> Lender rejected the deal layout due to credit parameters or term thresholds.
                      </div>
                      <button 
                        onClick={() => handleAutosave({ status: "DRAFT", currentStep: "FINANCE" })} 
                        className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold text-xs hover:shadow-lg shadow-md transition-all"
                      >
                        Adjust Lender Program
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* APPROVED / CONTRACTED / FUNDED STEPS */}
              {["APPROVED", "CONTRACTED", "FUNDED", "READY_FOR_DELIVERY", "DELIVERED"].includes(step) && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                      <span className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">🚚</span> Delivery Configuration
                    </h2>
                    <p className="text-slate-400 text-xs mt-1">Configure logistics and execute final signatures.</p>
                  </div>
                  
                  <div className="space-y-4 bg-slate-50 p-5 rounded-2xl border border-slate-100">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Select Delivery Method</label>
                      <select 
                        className="w-full border border-slate-200 rounded-xl p-2.5 text-sm bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all"
                        value={dealState.deliveryMethod || "Pickup"}
                        onChange={(e) => handleAutosave({ deliveryMethod: e.target.value })}
                      >
                        <option value="Pickup">Customer Pickup</option>
                        <option value="Home">Home Delivery</option>
                      </select>
                    </div>

                    <div className="space-y-3 pt-3 border-t border-slate-200/60">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-500 font-semibold">Retail Contract Signed</span>
                        {dealState.status === "APPROVED" ? (
                          <button onClick={() => handleAutosave({ status: "CONTRACTED" })} className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all">Sign Contract</button>
                        ) : (
                          <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-100/50 rounded-lg text-xs font-semibold">✓ Completed</span>
                        )}
                      </div>
                      
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-500 font-semibold">Lender Loan Funding</span>
                        {dealState.status === "CONTRACTED" ? (
                          <button onClick={() => handleAutosave({ status: "FUNDED" })} className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all">Mark Funded</button>
                        ) : ["APPROVED"].includes(dealState.status) ? (
                          <span className="text-slate-400 italic">Awaiting contract signature</span>
                        ) : (
                          <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-100/50 rounded-lg text-xs font-semibold">✓ Funded</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-slate-100">
                    {["FUNDED", "READY_FOR_DELIVERY", "CONTRACTED"].includes(dealState.status) ? (
                      <button 
                        onClick={() => handleAutosave({ status: "COMPLETED" })}
                        className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-2.5 rounded-xl font-bold hover:shadow-lg shadow-md transition-all text-sm"
                      >
                        Complete Deal & Deliver
                      </button>
                    ) : (
                      <button disabled className="bg-slate-200 text-slate-400 px-6 py-2.5 rounded-xl font-bold cursor-not-allowed text-xs">
                        Funding Pending
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* COMPLETED STEP */}
              {step === "COMPLETED" && (
                <div className="space-y-6 text-center py-10">
                  <div className="w-16 h-16 bg-emerald-50 text-emerald-500 border border-emerald-100 rounded-full flex items-center justify-center text-3xl mx-auto shadow-md animate-bounce">
                    🎉
                  </div>
                  <div>
                    <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight">Deal Fully Finalized!</h2>
                    <p className="text-slate-400 text-sm mt-2 max-w-xs mx-auto leading-relaxed">
                      Vehicle has been scheduled for delivery. Financial contract funding records are locked.
                    </p>
                  </div>
                  <button 
                    onClick={() => { setActiveDealId(null); fetchDealsAndCars(); }} 
                    className="mt-6 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl font-bold text-xs shadow-md transition-all"
                  >
                    Back to Deals List
                  </button>
                </div>
              )}
              
            </div>
          </div>
        </div>

        {/* Right Sidebar Utility - Link / Calculation Breakdown / AI Scenario */}
        <div className="w-80 bg-white border-l border-slate-200/80 flex flex-col h-full shadow-xl flex-shrink-0">
          {/* Customer live link & Kiosk Handoff */}
          <div className="p-6 bg-slate-900 text-white flex-shrink-0 relative overflow-hidden space-y-4">
            <div className="absolute right-0 top-0 w-32 h-32 bg-indigo-600/10 rounded-full blur-2xl"></div>
            
            {/* Live Read-Only view link */}
            <div>
              <h3 className="font-bold text-xs uppercase tracking-wider text-slate-400">Customer Live View</h3>
              <div className="bg-slate-950 rounded-xl p-3 text-xs flex items-center justify-between mt-2 border border-slate-800/80 select-all">
                <span className="truncate mr-3 text-slate-400 font-mono text-[11px]">{linkUrl}</span>
                <button 
                  onClick={() => handleCopyLink(linkUrl)} 
                  className={`flex-shrink-0 p-1.5 rounded-lg transition-all ${
                    copied ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-indigo-400 hover:text-indigo-300 hover:bg-slate-700'
                  }`}
                >
                  {copied ? <FaCheck size={10} /> : <FaCopy size={10} />}
                </button>
              </div>
            </div>

            {/* Interactive Kiosk view link */}
            <div>
              <h3 className="font-bold text-xs uppercase tracking-wider text-slate-400">Interactive Customer Kiosk</h3>
              <div className="bg-slate-950 rounded-xl p-3 text-xs flex items-center justify-between mt-2 border border-slate-800/80 select-all">
                <span className="truncate mr-3 text-slate-400 font-mono text-[11px]">{kioskUrl}</span>
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(kioskUrl);
                    setCopiedKiosk(true);
                    setTimeout(() => setCopiedKiosk(false), 2000);
                    toast.success("Kiosk link copied!");
                  }} 
                  className={`flex-shrink-0 p-1.5 rounded-lg transition-all ${
                    copiedKiosk ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-indigo-400 hover:text-indigo-300 hover:bg-slate-700'
                  }`}
                >
                  {copiedKiosk ? <FaCheck size={10} /> : <FaCopy size={10} />}
                </button>
              </div>
            </div>

            <div className="flex gap-4 border-t border-slate-800 pt-3">
              <a 
                href={linkUrl} 
                target="_blank" 
                rel="noreferrer" 
                className="text-[10px] text-indigo-400 hover:text-indigo-300 font-semibold hover:underline flex items-center gap-1"
              >
                Live View <FaExternalLinkAlt size={8} />
              </a>
              <button 
                onClick={() => setShowKioskQrModal(true)}
                className="text-[10px] text-indigo-400 hover:text-indigo-300 font-semibold hover:underline flex items-center gap-1 bg-transparent border-0"
              >
                Kiosk QR Code <FaQrcode size={8} />
              </button>
            </div>
            
            {/* Salesperson handoff takeover alert */}
            {dealState.kioskState?.handoffRequested && (
              <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/20 text-amber-300 rounded-xl text-[10px] leading-relaxed font-medium animate-pulse">
                ⚠️ Customer has structured deal terms on Kiosk and requested salesperson takeover.
              </div>
            )}
          </div>
          
          
          {/* Calculation snapshot list */}
          <div className="p-6 flex-1 overflow-y-auto space-y-6">
            <div>
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">Calculation Snapshot</h3>
              <div className="space-y-2.5 text-xs text-slate-600">
                <div className="flex justify-between">
                  <span className="text-slate-450">Sale Price</span>
                  <span className="font-semibold text-slate-750">${dealState?.vehicle?.salePrice?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-450">Accessories</span>
                  <span className="font-semibold text-slate-750">+${(dealState?.vehicle?.accessories || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-orange-600 bg-orange-50/50 p-2 rounded-xl border border-orange-100">
                  <span>Total Amount Due</span>
                  <span>${dealState.calculation?.totalAmountDue?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-440">Trade Equity</span>
                  <span className={`font-semibold \${dealState.calculation?.tradeEquity < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                    ${dealState.calculation?.tradeEquity?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-440">Down Payment</span>
                  <span className="font-bold text-emerald-600">-${dealState.finance?.downPayment?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between font-bold text-orange-600 bg-orange-50/50 p-2 rounded-xl border border-orange-100">
                  <span>Base Amount Financed</span>
                  <span>${dealState.calculation?.baseAmountFinanced?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-440">F&I Protection Total</span>
                  <span className="font-bold text-purple-650">+${dealState.calculation?.productTotal?.toLocaleString() || 0}</span>
                </div>
                
                <div className="pt-2 mt-2 border-t border-slate-100 font-black text-slate-800 text-sm">
                  <div className="flex justify-between">
                    <span>Total Amount Financed</span>
                    <span>${dealState.calculation?.totalAmountFinanced?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>

              {dealState.calculation?.payment > 0 && (
                <div className="mt-5 bg-gradient-to-br from-indigo-50/50 to-blue-50/30 border border-indigo-100/50 rounded-2xl p-4 text-center shadow-sm relative overflow-hidden">
                  <p className="text-[10px] text-indigo-600 font-extrabold uppercase tracking-wider mb-1">Estimated Payment</p>
                  <p className="text-3xl font-black text-indigo-700 tracking-tight">${dealState.calculation.payment.toLocaleString()}</p>
                  <p className="text-[10px] text-indigo-500/80 font-bold mt-1.5">{dealState.lender?.term || dealState.finance?.term} mos @ {dealState.lender?.apr || dealState.finance?.apr}% APR</p>
                </div>
              )}
            </div>

            {/* AI Assistant Section */}
            {dealState.aiAnalysis && (
              <div className="pt-6 border-t border-slate-100 space-y-4">
                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <span className="p-1 bg-indigo-50 rounded-lg text-indigo-600"><FaRobot size={12} /></span> AI Deal Assistant
                </h3>
                
                {dealState.aiAnalysis.warnings && dealState.aiAnalysis.warnings.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[9px] font-extrabold text-red-500 uppercase tracking-wider">Alerts & Rules:</p>
                    {dealState.aiAnalysis.warnings.map((w, idx) => (
                      <div key={idx} className="p-2.5 bg-red-50/80 border border-red-100 text-[10px] text-red-700 rounded-xl leading-relaxed">
                        ⚠️ {w}
                      </div>
                    ))}
                  </div>
                )}

                {dealState.aiAnalysis.recommendations?.scenarios && dealState.aiAnalysis.recommendations.scenarios.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-[9px] font-extrabold text-indigo-500 uppercase tracking-wider">Alternative Scenarios:</p>
                    {dealState.aiAnalysis.recommendations.scenarios.map((sc, idx) => (
                      <div key={idx} className="p-4 bg-slate-50 border border-slate-200/60 rounded-2xl text-[11px] space-y-2.5 hover:border-indigo-200 transition-all shadow-sm">
                        <div>
                          <div className="font-extrabold text-slate-800 text-xs">{sc.name}</div>
                          <div className="text-slate-400 text-[10px] font-medium mt-0.5">
                            {sc.lenderName} ({sc.apr}% for {sc.term} mos)
                          </div>
                        </div>
                        <div className="font-extrabold text-indigo-600 text-xs bg-indigo-50/50 p-2 rounded-lg text-center">
                          Payment: ${sc.payment?.toLocaleString()}/mo
                        </div>
                        {sc.fAndIProducts?.length > 0 && (
                          <div className="text-[10px] text-purple-650 font-bold">
                            F&I: {sc.fAndIProducts.join(", ")}
                          </div>
                        )}
                        <button
                          onClick={async () => {
                            // Find the plan object from our loaded plans state
                            const selectedPlan = plans.find(p => p.apr === sc.apr && p.term === sc.term);
                            const selectedFAndIObjects = fandiList.filter(f => sc.fAndIProducts.includes(f.name));
                            
                            if (selectedPlan) {
                              handleAutosave({
                                lender: {
                                  ...dealState.lender,
                                  planId: selectedPlan._id,
                                  apr: selectedPlan.apr,
                                  term: selectedPlan.term
                                },
                                fAndI: {
                                  ...dealState.fAndI,
                                  products: selectedFAndIObjects.map(f => f._id)
                                }
                              });
                              toast.success(`Applied ${sc.name}!`);
                            } else {
                              toast.error("Plan terms mismatch inventory records.");
                            }
                          }}
                          className="w-full bg-indigo-600 text-white font-bold py-2 rounded-xl text-xs hover:shadow-lg shadow-md transition-all"
                        >
                          Apply Scenario
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Kiosk QR Code Modal */}
        {showKioskQrModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in-up">
            <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-8 w-full max-w-sm overflow-hidden relative text-center text-slate-800">
              <button 
                onClick={() => setShowKioskQrModal(false)}
                className="absolute top-6 right-6 text-slate-400 hover:text-slate-650 p-1.5 hover:bg-slate-100 rounded-full transition-colors border-0 outline-none"
              >
                <FaTimes size={16} />
              </button>
              
              <h2 className="text-xl font-bold tracking-tight mb-1 text-slate-800">Kiosk Deal QR Code</h2>
              <p className="text-slate-400 text-xs mb-6 font-medium">
                Deal #{dealState.dealNumber} - {dealState.vehicle.year} {dealState.vehicle.make}
              </p>
              
              <div className="bg-slate-50 border border-slate-100 p-6 rounded-2xl inline-block shadow-inner mb-6">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(kioskUrl)}`}
                  alt="Kiosk QR Code"
                  className="w-48 h-48 mx-auto"
                />
              </div>
              
              <div className="text-slate-500 text-[11px] leading-relaxed mb-4">
                Scan this QR code using a tablet or mobile phone to open the Customer Kiosk deal configuration workspace for this session.
              </div>
              
              <button
                onClick={() => {
                  navigator.clipboard.writeText(kioskUrl);
                  toast.success("Kiosk URL copied!");
                  setShowKioskQrModal(false);
                }}
                className="w-full bg-slate-800 hover:bg-slate-900 text-white font-semibold py-2.5 rounded-xl text-sm transition-all"
              >
                Copy Link instead
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Dashboard Overview (Default Listing View)
  return (
    <div className="p-8 h-full w-full bg-slate-50/50 flex flex-col overflow-y-auto animate-fade-in-up">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
            <span className="p-2.5 bg-blue-50 text-blue-600 rounded-2xl shadow-sm border border-blue-100">
              <FaFileInvoiceDollar size={24} />
            </span>
            Deals Management
          </h1>
          <p className="text-slate-500 text-xs mt-1 leading-relaxed">
            Create structured deal sheets, configure financial rules, and monitor active workflows.
          </p>
        </div>
        {/* Claim Deal Form + Launch Kiosk + Create Draft Button */}
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="flex gap-2">
            <input 
              type="text" 
              value={claimId}
              onChange={(e) => setClaimId(e.target.value.toUpperCase())}
              placeholder="Enter Deal # (e.g. D873940)"
              className="bg-white border border-slate-200 rounded-xl px-3.5 py-2 text-xs outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/10 font-mono tracking-wider w-48 shadow-sm text-slate-800"
            />
            <button 
              onClick={handleClaimDeal}
              className="bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold px-4 py-2 rounded-xl border border-slate-700 shadow-sm transition-all cursor-pointer"
            >
              Claim Deal
            </button>
          </div>
          <button 
            onClick={() => {
              const user = JSON.parse(localStorage.getItem("user") || "{}");
              const spName = user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : (user.name || "Salesperson");
              const spId = user._id || user.id || "SP-ACTIVE";
              navigate(`/kiosk?salespersonId=${encodeURIComponent(spId)}&salespersonName=${encodeURIComponent(spName)}`);
            }}
            className="bg-slate-900 hover:bg-slate-800 text-cyan-300 border border-cyan-500/40 px-4 py-2.5 rounded-xl font-semibold hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 shadow-sm transition-all duration-200 flex items-center gap-2 text-sm cursor-pointer"
            title="Open Kiosk UI with automatic deal assignment to you"
          >
            <FaTabletAlt size={13} className="text-cyan-400" /> Launch Kiosk Mode
          </button>
          <button 
            onClick={handleCreateDraft} 
            className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 shadow-md transition-all duration-200 flex items-center gap-2 text-sm cursor-pointer"
          >
            <FaPlus size={12} /> Create Deal Draft
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200/80 text-slate-500 text-xs font-bold uppercase tracking-wider">
                <th className="px-6 py-4">Deal #</th>
                <th className="px-6 py-4">Customer</th>
                <th className="px-6 py-4">Vehicle</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-slate-400 text-sm">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></span>
                      Loading deals...
                    </div>
                  </td>
                </tr>
              ) : deals.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-400 text-sm italic">
                    No deals structured yet. Click "Create Deal Draft" to structure your first sheet.
                  </td>
                </tr>
              ) : (
                deals.map((deal) => (
                  <tr key={deal._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 font-bold text-slate-800 text-sm">{deal.dealNumber}</td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-850 text-sm">{deal.customer?.name || "Draft / Unnamed"}</div>
                      <div className="text-xs text-slate-400 font-medium">{deal.customer?.phone || "-"}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500 font-medium">
                      {deal.vehicle?.year ? `${deal.vehicle.year} ${deal.vehicle.make} ${deal.vehicle.model}` : "Vehicle Pending"}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2.5 py-1 border rounded-lg text-xs font-bold uppercase ${getStatusStyle(deal.status)}`}>
                        {deal.status?.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button 
                        onClick={() => handleResumeDeal(deal._id)} 
                        className="text-indigo-600 hover:text-indigo-800 font-bold text-xs tracking-wide bg-indigo-50 hover:bg-indigo-100/80 px-3.5 py-2 rounded-xl transition-colors border border-indigo-200/20"
                      >
                        {deal.status === 'DRAFT' ? 'Continue Deal' : 'Open Workspace'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default D2Deals;
